// Factor computation pipeline. Turns raw FMP statements + Bloomberg overrides
// into the StockFactors row that scoring consumes. ~530 lines of arithmetic;
// kept in its own file so scoring.ts isn't drowning in factor math.

import type { StockData, StockFactors, StockOverride } from './types';
import { isBankLike, isProperBank, isREITLike } from './peer-groups';
import { prepareEstimates } from './factor-pipeline-estimates';
import { readPeerGroupFactors } from './_factor-pipeline/peer-group-factors';
import { computePriceMomentum } from './_factor-pipeline/price-momentum';
import {
  fwdProfitability,
  fwdNetMargin as computeFwdNetMargin,
  fwdDebtToEbitda as computeFwdDebtToEbitda,
  fwdShareholderReturns,
  fwdNextQtrEpsGrowth,
} from './_factor-pipeline/forward-factors';
import { safe, cagr, signedGrowth, stdDev } from './_factor-pipeline/utils';

export function computeFactors(d: StockData, override?: StockOverride): StockFactors {
  const inc = d.income;
  const bal = d.balance;
  const cf = d.cashflow;

  // Estimate filtering, override application, NTM blending, year-2 fallback —
  // all live in factor-pipeline-estimates.ts so the body of computeFactors is
  // factor arithmetic only.
  const est = prepareEstimates(d, override);

  const i0 = inc[0], i1 = inc[1], i2 = inc[2], i3 = inc[3], i4 = inc[4];
  const b0 = bal[0], b1 = bal[1];
  const c0 = cf[0], c3 = cf[3];

  const taxRate = (i0 && i0.incomeBeforeTax && i0.incomeBeforeTax !== 0)
    ? Math.min(Math.max(i0.incomeTaxExpense / i0.incomeBeforeTax, 0), 0.5) : 0.21;

  const ic0 = b0 ? b0.totalStockholdersEquity + b0.totalDebt : null;

  // Profitability (7)
  // Negative equity + profitable = company returned more capital than retained — treat as top-tier ROE
  const rawRoe = (i0 && b0 && b0.totalStockholdersEquity) ? safe(i0.netIncome / b0.totalStockholdersEquity) : null;
  const roe = (rawRoe !== null && rawRoe < 0 && b0 && b0.totalStockholdersEquity < 0 && i0 && i0.netIncome > 0)
    ? 1.0  // cap at 100% — signals strong capital return, not poor profitability
    : rawRoe;
  const roa = (i0 && b0 && b0.totalAssets) ? safe(i0.netIncome / b0.totalAssets) : null;
  // Negative invested capital + profitable = same distortion as ROE — treat as top-tier
  const rawRoic = (i0 && ic0 && ic0 !== 0) ? safe(i0.ebit * (1 - taxRate) / ic0) : null;
  const roic = (rawRoic !== null && rawRoic < 0 && ic0 !== null && ic0 < 0 && i0 && i0.ebit > 0)
    ? 0.5  // cap at 50% — strong capital efficiency signal
    : rawRoic;
  const grossProfitToAssets = (i0 && b0 && b0.totalAssets) ? safe(i0.grossProfit / b0.totalAssets) : null;
  const grossMargin = (i0 && i0.revenue) ? safe(i0.grossProfit / i0.revenue) : null;
  const netMargin = (i0 && i0.revenue) ? safe(i0.netIncome / i0.revenue) : null;
  const cfoToAssets = (c0 && b0 && b0.totalAssets) ? safe(c0.operatingCashFlow / b0.totalAssets) : null;

  // Accruals ratio: (netIncome - operatingCashFlow) / totalAssets — lower = more cash-backed earnings (Sloan 1996)
  const accrualsRatio = (i0 && c0 && b0 && b0.totalAssets) ? safe((i0.netIncome - c0.operatingCashFlow) / b0.totalAssets) : null;

  // Asset growth: YoY % change in total assets — aggressive growers tend to underperform (Fama-French CMA)
  const assetGrowth = (b0 && b1 && b1.totalAssets && b1.totalAssets !== 0) ? safe((b0.totalAssets - b1.totalAssets) / b1.totalAssets) : null;

  // Financial-specific profitability. Prefer direct BBG ratios when available
  // (T12_NET_INT_MARGIN / EFF_RATIO — added 2026-05-13 mnemonic session),
  // fall back to derived computation. Direct fields are bank-only and null
  // for non-banks, which is the correct behavior.
  const nim = override?.nimT12 != null
    ? safe(override.nimT12 / 100)  // BBG returns NIM as a percent; normalize to ratio for z-score consistency
    : ((i0 && b0 && b0.totalAssets && i0.netInterestIncome) ? safe(i0.netInterestIncome / b0.totalAssets) : null);
  const efficiencyRatio = override?.effRatio != null
    ? safe(override.effRatio / 100)
    : ((i0 && i0.revenue && i0.operatingExpenses) ? safe(i0.operatingExpenses / i0.revenue) : null);
  // Loan loss provisions / net interest income — bank earnings quality (lower = better, invert).
  // Replaces accrualsRatio for banks (CFO isn't meaningful for banks — deposits distort the accruals signal).
  // Prefer direct BBG PROV_LOAN_LOSSES_TO_NII when present (cleaner than computing from
  // separate loanLossProvisions / netInterestIncome fields which can have currency or
  // mnemonic mismatches on foreign listings).
  const provisionsToNII = override?.provLoanLossesToNii != null
    ? safe(override.provLoanLossesToNii / 100)
    : ((i0 && i0.netInterestIncome && i0.loanLossProvisions != null && i0.netInterestIncome !== 0)
        ? safe(i0.loanLossProvisions / i0.netInterestIncome) : null);

  // Per-peer-group factor reads — extracted to
  // lib/_factor-pipeline/peer-group-factors.ts. Bank + REIT + Universal Safety
  // + Innovation/Dilution. Gating to peer group happens at the return block
  // below (properBank / reitLike / bankLike).
  const pg = readPeerGroupFactors(d, override);

  // Growth (8) — 4 forward, 4 backward

  // Forward: 1-year EPS growth (analyst consensus vs last reported)
  // Bloomberg EPS (annual, most recent fiscal year) — use as trailing base for growth calcs
  // More current and consistent with forward estimates (both from Bloomberg)
  const bbgEpsNow = override?.epsNow ?? null;
  const bbgEpsPrior = override?.epsPrior ?? null;
  const trailingEps = bbgEpsNow ?? i0?.epsDiluted ?? null;
  const priorEps = bbgEpsPrior ?? i1?.epsDiluted ?? null;

  let fwdEpsGrowth: number | null = null;
  if (est?.length > 0 && trailingEps && trailingEps !== 0) {
    fwdEpsGrowth = safe((est[0].epsAvg - trailingEps) / Math.abs(trailingEps));
  } else if (trailingEps && priorEps && priorEps !== 0) {
    fwdEpsGrowth = safe((trailingEps - priorEps) / Math.abs(priorEps));
  }
  // Z-score clamping at [-3, +3] handles outliers — no need to cap forward estimates
  // (backward growth IS capped at ±100% below to prevent turnaround base effects)

  // Bank-flavored financials flag — hoisted up for the revenue calcs below.
  // BBG returns NET revenue in `BEST_SALES` forward consensus, but
  // `SALES_REV_TURN` trailing is GROSS revenue (gross interest/trading income
  // before financing-cost netting). Mixing the two produces garbage CAGR for
  // banks, i-banks, brokers, and asset managers (JPM −14%, BNS.TO −34%,
  // GS −27% — verified via direct SAPI 2026-05-26). Switch to a forward-to-
  // forward growth proxy below (est[0]→est[1], both BEST_SALES, same net-
  // revenue definition). Long-term fix is to pull `NET_REV` mnemonic for
  // these names in the BBG fetcher and use it as the trailing baseline.
  const isPropBankForRev = isBankLike(d);

  // Forward: 1-year revenue PER SHARE growth (Bloomberg SPS vs prior year SPS)
  // Revenue per share normalizes for share count changes — more accurate signal than raw revenue growth
  // Falls back to raw revenue growth if Bloomberg SPS not available
  let fwdRevGrowth: number | null = null;
  if (override?.revPerShareGrowth != null) {
    fwdRevGrowth = override.revPerShareGrowth;
  } else if (isPropBankForRev && est?.length > 1 && est[0].revenueAvg > 0 && est[1].revenueAvg > 0) {
    // Banks: use forward-to-forward growth (FY+1 → FY+2) — same definition
    // both sides, single-source BBG. Approximates 1-year growth.
    fwdRevGrowth = safe((est[1].revenueAvg - est[0].revenueAvg) / est[0].revenueAvg);
  } else if (est?.length > 0) {
    const baseRev = override?.trailingRevenue ?? (i0 ? i0.revenue : null);
    if (baseRev && baseRev > 0) {
      fwdRevGrowth = safe((est[0].revenueAvg - baseRev) / baseRev);
    }
  } else if (i0 && i1 && i1.revenue) {
    fwdRevGrowth = safe((i0.revenue - i1.revenue) / i1.revenue);
  }

  // Forward: 2-year EPS CAGR (Bloomberg trailing EPS → year-2 estimate)
  let fwdEpsCagr2yr: number | null = null;
  if (est?.length > 1 && trailingEps) {
    fwdEpsCagr2yr = cagr(trailingEps, est[1].epsAvg, 2)
      ?? signedGrowth(trailingEps, est[1].epsAvg);
  }

  // Forward: 2-year revenue CAGR (current revenue → year-2 estimate)
  // Use override trailing revenue for ADRs where FMP revenue is in local currency.
  // For banks: use FY+1 → FY+2 growth rate as the per-year CAGR (same as the
  // 1-year fwdRevGrowth above) — bypasses the gross-vs-net definitional gap.
  let fwdRevCagr2yr: number | null = null;
  const trailingRevenue = override?.trailingRevenue ?? (i0 ? i0.revenue : null);
  if (isPropBankForRev && est?.length > 1 && est[0].revenueAvg > 0 && est[1].revenueAvg > 0) {
    fwdRevCagr2yr = safe((est[1].revenueAvg - est[0].revenueAvg) / est[0].revenueAvg);
  } else if (est?.length > 1 && trailingRevenue && trailingRevenue > 0 && est[1].revenueAvg > 0) {
    fwdRevCagr2yr = cagr(trailingRevenue, est[1].revenueAvg, 2);
  }

  // Forward: 3-year EPS CAGR (Bloomberg trailing EPS → year-3 estimate from BBG BEST_EPS_3BF) — added 2026-04-24
  // Symmetry with Valuation's fwdPE3yr factor. Rewards real 3-year compounders.
  let fwdEpsCagr3yr: number | null = null;
  if (override?.fwdEps3yr != null && trailingEps) {
    fwdEpsCagr3yr = cagr(trailingEps, override.fwdEps3yr, 3)
      ?? signedGrowth(trailingEps, override.fwdEps3yr);
  }

  // Forward operating leverage = fwdEpsCagr2yr − fwdRevCagr2yr — added 2026-04-24
  // Differentiates margin-expanding compounders (EPS growth > revenue growth) from revenue-only growers
  // (margins compressing, growth bought through dilution or capex). Clamped to [−0.30, +0.30].
  const fwdOperLeverage = (fwdEpsCagr2yr != null && fwdRevCagr2yr != null)
    ? Math.max(-0.30, Math.min(0.30, fwdEpsCagr2yr - fwdRevCagr2yr))
    : null;

  // Earnings revision momentum — % change in Bloomberg consensus EPS since oldest snapshot
  // Computed by refresh.ts from bbg-estimates-history/ snapshots and passed via override
  const epsRevision: number | null = override?.epsRevision ?? null;

  // EPS-revision velocity family (2026-05-22). Computed in
  // scripts/data-pipeline/compute-revision-velocity.ts from daily +
  // monthly BBG snapshots and passed via override. Not yet wired into
  // PILLAR_FACTORS — informational side-channel for the Story drawer
  // + Movers tab. See lib/eps-revision-velocity.ts.
  const revisionDelta7d: number | null = override?.revisionDelta7d ?? null;
  const revisionDelta30d: number | null = override?.revisionDelta30d ?? null;
  const revisionDelta90d: number | null = override?.revisionDelta90d ?? null;
  const revisionAcceleration: number | null = override?.revisionAcceleration ?? null;

  // Insider trading — market-cap normalized net buy yield
  // (buyValue - sellValue) / marketCap — $86K selling on $115B company = noise, $10M buying on $5B = strong signal
  // Falls back to raw ratio if market cap not available
  let insiderNetBuy: number | null = null;
  if (override?.insiderBuyValue != null && override?.insiderSellValue != null && d.marketCap && d.marketCap > 0) {
    const netValue = (override.insiderBuyValue ?? 0) - (override.insiderSellValue ?? 0);
    insiderNetBuy = netValue / d.marketCap;
  } else if (override?.insiderNetBuy != null) {
    insiderNetBuy = override.insiderNetBuy;
  }

  // Earnings surprise — (actual - consensus) / |consensus| from last report
  const earningsSurprise: number | null = override?.earningsSurprise ?? null;

  // Backward-looking growth — clamped to [-1, +1] to prevent turnaround base effects
  // (e.g., company lost money in 2022, now profitable → 1617% "growth" is misleading)
  let epsCAGR3yr = (i0 && i3) ? (cagr(i3.epsDiluted, i0.epsDiluted, 3) ?? signedGrowth(i3.epsDiluted, i0.epsDiluted)) : null;
  if (epsCAGR3yr !== null) epsCAGR3yr = Math.max(-1, Math.min(1, epsCAGR3yr));
  let fcfGrowth3yr = (c0 && c3) ? (cagr(c3.freeCashFlow, c0.freeCashFlow, 3) ?? signedGrowth(c3.freeCashFlow, c0.freeCashFlow)) : null;
  if (fcfGrowth3yr !== null) fcfGrowth3yr = Math.max(-1, Math.min(1, fcfGrowth3yr));
  const revenueGrowth3yr = (i0 && i3 && i3.revenue > 0) ? cagr(i3.revenue, i0.revenue, 3) : null;

  // Gross margin expansion: current GM - GM 3yr ago — clamped to [-0.30, +0.30] (30pp max)
  const gm0 = (i0 && i0.revenue) ? i0.grossProfit / i0.revenue : null;
  const gm3 = (i3 && i3.revenue) ? i3.grossProfit / i3.revenue : null;
  let grossMarginExpansion3yr = (gm0 !== null && gm3 !== null) ? safe(gm0 - gm3) : null;
  if (grossMarginExpansion3yr !== null) grossMarginExpansion3yr = Math.max(-0.30, Math.min(0.30, grossMarginExpansion3yr));

  // Operating margin expansion: current OM - OM 3yr ago — clamped to [-0.30, +0.30]
  const om0 = (i0 && i0.revenue) ? i0.ebit / i0.revenue : null;
  const om3 = (i3 && i3.revenue) ? i3.ebit / i3.revenue : null;
  let opMarginExpansion3yr = (om0 !== null && om3 !== null) ? safe(om0 - om3) : null;
  if (opMarginExpansion3yr !== null) opMarginExpansion3yr = Math.max(-0.30, Math.min(0.30, opMarginExpansion3yr));

  // Safety (8)
  let altmanZ: number | null = null;
  if (i0 && b0 && b0.totalAssets && b0.totalLiabilities) {
    const wc = b0.totalCurrentAssets - b0.totalCurrentLiabilities;
    const ta = b0.totalAssets;
    altmanZ = safe(
      1.2 * (wc / ta) +
      1.4 * (b0.retainedEarnings / ta) +
      3.3 * (i0.ebit / ta) +
      0.6 * (d.marketCap / b0.totalLiabilities) +
      1.0 * (i0.revenue / ta)
    );
  }

  let piotroski: number | null = null;
  if (i0 && i1 && b0 && b1 && c0) {
    let score = 0;
    const ta0 = b0.totalAssets, ta1 = b1.totalAssets;
    const roa0 = ta0 ? i0.netIncome / ta0 : 0;
    const roa1 = ta1 ? i1.netIncome / ta1 : 0;
    const cfo = c0.operatingCashFlow;
    if (roa0 > 0) score++;
    if (cfo > 0) score++;
    if (roa0 > roa1) score++;
    if (cfo > i0.netIncome) score++;
    if ((b0.totalDebt / ta0) < (b1.totalDebt / ta1)) score++;
    const cr0 = b0.totalCurrentLiabilities ? b0.totalCurrentAssets / b0.totalCurrentLiabilities : 0;
    const cr1 = b1.totalCurrentLiabilities ? b1.totalCurrentAssets / b1.totalCurrentLiabilities : 0;
    if (cr0 > cr1) score++;
    if ((c0.netCommonStockIssuance ?? 0) <= 0) score++;
    const pgm0 = i0.revenue ? i0.grossProfit / i0.revenue : 0;
    const pgm1 = i1.revenue ? i1.grossProfit / i1.revenue : 0;
    if (pgm0 > pgm1) score++;
    const at0 = ta0 ? i0.revenue / ta0 : 0;
    const at1 = ta1 ? i1.revenue / ta1 : 0;
    if (at0 > at1) score++;
    piotroski = score;
  }

  // Debt/EBITDA — penalty (high = risky) when EBITDA is negative
  const debtToEbitda = (i0 && b0 && i0.ebitda)
    ? (i0.ebitda > 0 ? safe(b0.totalDebt / i0.ebitda) : safe(b0.totalDebt > 0 ? 20 : 0))
    : null;
  // Interest coverage — 999 for debt-free companies (no interest expense)
  const interestCoverage = (i0)
    ? (i0.interestExpense > 0 ? safe(i0.ebit / i0.interestExpense) : 999)
    : null;

  const epsVals = [i0, i1, i2, i3, i4].filter(Boolean).map(x => x!.epsDiluted).filter(v => v !== null && isFinite(v));
  const epsVariability = epsVals.length >= 3 ? stdDev(epsVals) : null;

  const betaVal = safe(d.beta);
  const rawCR = (b0 && b0.totalCurrentLiabilities) ? safe(b0.totalCurrentAssets / b0.totalCurrentLiabilities) : null;
  const currentRatio = (rawCR !== null && rawCR > 100) ? null : rawCR; // discard garbage values

  // FCF/Debt — using depreciation as maintenance capex proxy: (OCF - D&A) / Total Debt
  let fcfToDebt: number | null = null;
  if (c0 && b0 && b0.totalDebt > 0) {
    const dep = c0.depreciationAndAmortization ?? 0;
    const maintenanceFcf = c0.operatingCashFlow - dep;
    fcfToDebt = safe(maintenanceFcf / b0.totalDebt);
  }

  // Financial-specific safety
  const equityToAssets = (b0 && b0.totalAssets) ? safe(b0.totalStockholdersEquity / b0.totalAssets) : null;
  const retainedEarningsToAssets = (b0 && b0.totalAssets) ? safe(b0.retainedEarnings / b0.totalAssets) : null;

  // Payout (3)
  // Net buyback yield — uses Bloomberg current shares (millions) vs FMP prior year when available.
  // Bloomberg current shares are far more accurate than FMP's cash flow statement buyback figures,
  // which miss SBC dilution. Net share reduction = true capital return signal.
  // Formula: (prior_shares - current_shares) / prior_shares — positive = net buyback, negative = net dilution
  const shares1 = i1?.weightedAverageShsOutDil; // prior year from FMP income statement
  const bbgCurrentSharesM = override?.currentSharesM;
  const shares0 = i0?.weightedAverageShsOutDil; // current year FMP (fallback)

  const buybackYield = (() => {
    if (bbgCurrentSharesM && shares1 && shares1 > 0) {
      // Bloomberg current shares (millions) vs FMP prior year shares (raw units)
      // FMP shares are in actual count (e.g. 15,408,095,000), BBG in millions (e.g. 14681.14)
      const bbgShares = bbgCurrentSharesM * 1e6;
      return safe((shares1 - bbgShares) / shares1); // positive when shares shrink
    }
    // Fallback: FMP vs FMP (less accurate, misses SBC netting)
    if (shares0 && shares1 && shares1 > 0) {
      return safe((shares1 - shares0) / shares1);
    }
    return null;
  })();

  const divPaid = c0 ? Math.abs(c0.commonDividendsPaid ?? 0) : 0;
  const dividendYield = d.marketCap > 0 ? safe(divPaid / d.marketCap) : null;

  // Net debt paydown yield = (prior net debt - current net debt) / market cap
  // Positive = company is deleveraging (good), Negative = adding debt (bad)
  const netDebt0 = b0 ? (b0.totalDebt - b0.cashAndCashEquivalents) : null;
  const netDebt1 = b1 ? (b1.totalDebt - b1.cashAndCashEquivalents) : null;
  const netDebtPaydownYield = (netDebt0 !== null && netDebt1 !== null && d.marketCap > 0)
    ? safe((netDebt1 - netDebt0) / d.marketCap)
    : null;

  // Valuation (7) — 4 forward, 3 trailing

  // Forward: Fwd P/E (1yr) — price / next-year consensus EPS
  const fwdEps = est?.length > 0 ? est[0].epsAvg : null;
  const fwdPE = (fwdEps && fwdEps > 0 && d.price) ? safe(d.price / fwdEps) : null;

  // Forward: Fwd P/E (2yr) — price / year-2 consensus EPS
  const fwdEps2 = est?.length > 1 ? est[1].epsAvg : null;
  const fwdPE2yr = (fwdEps2 && fwdEps2 > 0 && d.price) ? safe(d.price / fwdEps2) : null;

  // Forward: Fwd P/E (3yr) — price / BEST_EPS at 3BF horizon (Bloomberg consensus, year-3 fiscal estimate).
  // Rewards real compounders with low 3yr PE; speculative hyper-growers stay expensive even 3 years out.
  // Coverage limited to ~500-600/763 stocks (BBG 3BF coverage drops for small-caps and newer names).
  const fwdEps3 = override?.fwdEps3yr ?? null;
  const fwdPE3yr = (fwdEps3 && fwdEps3 > 0 && d.price) ? safe(d.price / fwdEps3) : null;

  // Forward: Fwd EV/Revenue — EV / next-year consensus revenue
  let fwdEvToRevenue: number | null = null;
  if (b0 && d.marketCap && est?.length > 0 && est[0].revenueAvg > 0) {
    const ev = d.marketCap + b0.totalDebt - b0.cashAndCashEquivalents;
    fwdEvToRevenue = safe(ev / est[0].revenueAvg);
  }

  // Forward: PEG — Fwd P/E / forward EPS growth rate (as whole number, e.g. 20% → 20)
  // Cap growth at 50% to prevent PEG collapsing for hyper-growers (PLTR, etc.)
  // Penalty (high PEG = expensive) when growth is negative or zero
  let pegRatio: number | null = null;
  if (fwdPE !== null && fwdEpsGrowth !== null) {
    if (fwdEpsGrowth > 0.02) {
      const cappedGrowth = Math.min(fwdEpsGrowth * 100, 50);
      pegRatio = safe(fwdPE / cappedGrowth);
    } else {
      pegRatio = 10; // penalty: no/negative growth with a positive P/E = expensive
    }
  }

  // Trailing: EV/EBITDA, EV/Gross Profit
  let evToEbitda: number | null = null;
  let evToGrossProfit: number | null = null;
  let ev: number | null = null;
  if (b0 && i0 && d.marketCap) {
    ev = d.marketCap + b0.totalDebt - b0.cashAndCashEquivalents;
    evToEbitda = (i0.ebitda > 0) ? safe(ev / i0.ebitda) : 100;
    evToGrossProfit = (i0.grossProfit > 0) ? safe(ev / i0.grossProfit) : null;
  }

  // Forward: TEV / BEST_EBITDA_1y (Bloomberg forward EBITDA). EV uses current marketCap + current net debt;
  // denominator is forward — matches Bloomberg BEST_EV_EBITDA convention and is the industry-standard forward multiple.
  const fwdEvToEbitda = (ev != null && override?.fwdEbitda != null && override.fwdEbitda > 0)
    ? safe(ev / override.fwdEbitda) : null;

  // Trailing: P/FCF — penalty (high = expensive) when FCF is negative
  const fcf0 = c0?.freeCashFlow ?? null;
  const pToFcf = (fcf0 && d.marketCap)
    ? (fcf0 > 0 ? safe(d.marketCap / fcf0) : 200)
    : null;

  // Financial-specific valuation: Price / Book
  const priceToBook = (b0 && b0.totalStockholdersEquity > 0 && d.marketCap)
    ? safe(d.marketCap / b0.totalStockholdersEquity) : null;

  // Financial-specific valuation: Price / Sales (replaces EV/Revenue for banks)
  const pToRevenue = (i0 && i0.revenue > 0 && d.marketCap)
    ? safe(d.marketCap / i0.revenue) : null;

  // Forward per-share valuation (Bloomberg consensus, buyback-adjusted) — added 2026-04-22
  // fwdPtoBook uses BBG's BEST_BPS_1y directly — no derivation needed
  const fwdPtoBook = (override?.fwdBookPerShare != null && override.fwdBookPerShare > 0 && d.price > 0)
    ? safe(d.price / override.fwdBookPerShare) : null;
  // fwdPtoSales derives forward SPS = BEST_SALES_1y / implied_forward_shares_1y (captures expected buybacks)
  const fwdPtoSales = (override?.fwdRev != null && override.fwdRev > 0
                       && override?.impliedShares1y != null && override.impliedShares1y > 0
                       && d.price > 0)
    ? safe(d.price / (override.fwdRev / override.impliedShares1y)) : null;

  // Forward factors — all extracted to _factor-pipeline/forward-factors.ts.
  // See that file for the detailed rationale on each (priorYrNextQEps vs
  // priorYrSameQEps off-by-one, dividend-indicated-yield vs analyst-consensus,
  // buyback-yield TTM-realized vs implied-shares chain).
  const nextQtrEpsGrowth = fwdNextQtrEpsGrowth(override);
  const { fwdRoe, fwdRoa } = fwdProfitability(override);
  const fwdNetMargin = computeFwdNetMargin(override);
  const fwdDebtToEbitda = computeFwdDebtToEbitda(override);
  const { fwdDividendYield, impliedBuybackYield } = fwdShareholderReturns(d, override);

  // Momentum + nearHigh + shortTermReversal — all from priceHistory.
  // Extracted to lib/_factor-pipeline/price-momentum.ts.
  const { momentum, nearHigh, shortTermReversal } = computePriceMomentum(d);

  // Sanity check: null out clearly broken valuation data (ADR currency mismatch, etc.)
  const sanityCheck = (v: number | null, min: number): number | null =>
    (v !== null && v < min) ? null : v;

  // Bank-like stocks don't report meaningful gross profit — null out dependent factors
  const bankLike = isBankLike(d);
  // Stricter subset for the 2026-05-14 regulator-style bank factors: only stocks
  // that actually run a deposit/loan book (or consumer receivables). Excludes
  // brokers, asset managers, exchanges, life insurers — they sit in the financial
  // peer group for relative comparability but don't have a loan book for tier1 /
  // NPL / provCoverage / ROTCE to discriminate on.
  const properBank = isProperBank(d);
  // REIT-specific factor gating (2026-05-14): FFO yield, occupancy, NOI growth,
  // P/FFO, P/NAV apply only to REITs. Non-REITs see null on these → 0 z-score.
  const reitLike = isREITLike(d);

  // Bloomberg fundamental overrides — applied after FMP computation, before
  // return. Preserves negative-equity ROE/ROIC rules above (override script
  // skips those cases).
  const ov = <T,>(k: keyof StockOverride, fallback: T): T =>
    (override?.[k] != null ? (override[k] as unknown as T) : fallback);
  const roe_f = ov('roe', roe);
  const roa_f = ov('roa', roa);
  const roic_f = ov('roic', roic);
  const grossMargin_f = ov('grossMargin', grossMargin);
  const netMargin_f = ov('netMargin', netMargin);
  const cfoToAssets_f = ov('cfoToAssets', cfoToAssets);
  const grossProfitToAssets_f = ov('grossProfitToAssets', grossProfitToAssets);
  const interestCoverage_f = ov('interestCoverage', interestCoverage);
  const currentRatio_f = ov('currentRatio', currentRatio);
  const debtToEbitda_f = ov('debtToEbitda', debtToEbitda);

  return {
    roe: roe_f, roa: roa_f, roic: bankLike ? null : roic_f,
    grossProfitToAssets: bankLike ? null : grossProfitToAssets_f,
    grossMargin: bankLike ? null : grossMargin_f,
    netMargin: netMargin_f,
    cfoToAssets: bankLike ? null : cfoToAssets_f,
    accrualsRatio: bankLike ? null : accrualsRatio,
    assetGrowth,
    nim, efficiencyRatio,
    provisionsToNII: bankLike ? provisionsToNII : null,
    // Bank-specific (2026-05-14). Gated on `properBank` (not `bankLike`) so
    // brokers/asset-managers/exchanges/life-insurers see null on these and
    // don't pollute the peer-z. See peer-groups.ts PROPER_BANK_INDUSTRIES.
    rotce: properBank ? pg.rotce : null,
    tier1CapRatio: properBank ? pg.tier1CapRatio : null,
    cet1Ratio: properBank ? pg.cet1Ratio : null,
    tceRatio: properBank ? pg.tceRatio : null,
    nplRatio: properBank ? pg.nplRatio : null,
    provCoverage: properBank ? pg.provCoverage : null,
    ncoRate: properBank ? pg.ncoRate : null,
    loanToDeposit: properBank ? pg.loanToDeposit : null,
    // REIT-specific. Gated on reitLike → null for non-REITs.
    ffoYield: reitLike ? pg.ffoYield : null,
    occupancyRate: reitLike ? pg.occupancyRate : null,
    sameStoreNoiGrowth: reitLike ? pg.sameStoreNoiGrowth : null,
    reAvgLeaseTerm: reitLike ? pg.reAvgLeaseTerm : null,
    pToFfo: reitLike ? pg.pToFfo : null,
    pToNav: reitLike ? pg.pToNav : null,
    // Universal Safety + Innovation/Dilution — apply across all peer groups.
    cdsSpread: pg.cdsSpread,
    shortInterestDaysToCover: pg.shortInterestDaysToCover,
    rdIntensity: pg.rdIntensity,
    sbcDilution: pg.sbcDilution,
    fwdEpsGrowth, fwdRevGrowth, fwdEpsCagr2yr, fwdRevCagr2yr, fwdEpsCagr3yr, fwdOperLeverage, epsRevision,
    revisionDelta7d, revisionDelta30d, revisionDelta90d, revisionAcceleration,
    epsCAGR3yr: (override?.epsCAGR3yr != null) ? Math.max(-1, Math.min(1, override.epsCAGR3yr)) : epsCAGR3yr,
    fcfGrowth3yr: (override?.fcfGrowth3yr != null) ? Math.max(-1, Math.min(1, override.fcfGrowth3yr)) : fcfGrowth3yr,
    grossMarginExpansion3yr: bankLike ? null : ((override?.grossMarginExpansion3yr != null) ? Math.max(-0.30, Math.min(0.30, override.grossMarginExpansion3yr)) : grossMarginExpansion3yr),
    opMarginExpansion3yr: bankLike ? null : ((override?.opMarginExpansion3yr != null) ? Math.max(-0.30, Math.min(0.30, override.opMarginExpansion3yr)) : opMarginExpansion3yr),
    revenueGrowth3yr: (override?.revenueGrowth3yr != null) ? override.revenueGrowth3yr : revenueGrowth3yr,
    altmanZ: bankLike ? null : altmanZ, piotroski, debtToEbitda: bankLike ? null : debtToEbitda_f, interestCoverage: interestCoverage_f, epsVariability, beta: betaVal, currentRatio: currentRatio_f, fcfToDebt,
    equityToAssets, retainedEarningsToAssets,
    dividendYield, buybackYield, netDebtPaydownYield,
    momentum, nearHigh, shortTermReversal, insiderNetBuy, earningsSurprise,
    nextQtrEpsGrowth,
    fwdPE: sanityCheck(fwdPE, 1), fwdPE2yr: sanityCheck(fwdPE2yr, 1), fwdPE3yr: sanityCheck(fwdPE3yr, 1),
    fwdEvToRevenue: bankLike ? null : fwdEvToRevenue, pegRatio: bankLike ? null : pegRatio, evToEbitda: bankLike ? null : evToEbitda, pToFcf,
    fwdEvToEbitda: bankLike ? null : fwdEvToEbitda,
    evToGrossProfit: bankLike ? null : evToGrossProfit,
    priceToBook: sanityCheck(priceToBook, 0.05),
    pToRevenue: bankLike ? pToRevenue : null,
    fwdPtoBook: sanityCheck(fwdPtoBook, 0.05),
    fwdPtoSales: bankLike ? null : sanityCheck(fwdPtoSales, 0.05),
    fwdRoe,
    fwdRoa: bankLike ? null : fwdRoa, // banks have wonky analyst ROA forecasts
    fwdNetMargin,
    fwdDebtToEbitda: bankLike ? null : fwdDebtToEbitda, // banks lack EBITDA; use equityToAssets for safety instead
    fwdDividendYield,
    impliedBuybackYield,
  };
}
